import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import Text from '~/components/core/TextComponents/Text';
import TextFieldFormik from '~/components/TextFieldFormik';
import SUPPORTED_CURRENCIES from '~/server_shared/generated-types/SUPPORTED_CURRENCIES';
import type {
  StripeConfigurationArguments,
  StripeConfigurationResponseStripeConfiguration,
} from '~/server_shared/openapi/data-contracts';

// Taken off the generated backend contract rather than restated, so a rail added server-side breaks
// the display-name map below (which must be exhaustive) instead of silently rendering nothing.
export type StripeRail = StripeConfigurationResponseStripeConfiguration['enabled_rails'][number];

// The strings must match `stripe_utils.RAILS[*].display_name`, which is what the adjuster-facing
// validation messages interpolate ("A SEPA Credit Transfer payout must be issued in EUR..."). One rail
// named two ways across the screens a user can see reads as two different rails.
export const STRIPE_RAIL_DISPLAY_NAMES: Record<StripeRail, string> = {
  uk_faster_payments: 'UK Faster Payments',
  eu_sepa_credit_transfer: 'SEPA Credit Transfer',
};

// `Object.keys` widens to `string[]`; the keys of an exhaustive `Record<StripeRail, string>` are
// exactly the rails, so the narrowing is safe and keeps one source for the option list.
const STRIPE_RAILS = Object.keys(STRIPE_RAIL_DISPLAY_NAMES) as StripeRail[];

// The EU rail is gated vendor-side, so a brand-new configuration starts on the UK rail alone.
const DEFAULT_ENABLED_RAILS: StripeRail[] = ['uk_faster_payments'];

const ENABLED_RAILS_FIELD_ID = 'enabled_rails';

// Stripe rejects an outbound payment whose statement descriptor falls outside these bounds, and the
// rejection lands on the payout rather than on this form, so the same numbers are enforced here and
// on the write route (`server/modules/stripe/schemas/__init__.py`).
const STATEMENT_DESCRIPTOR_MIN_LENGTH = 3;
const STATEMENT_DESCRIPTOR_MAX_LENGTH = 22;

// Masks the secret inputs. Applied through InputProps rather than a `type` prop because
// TextFieldFormik is untyped JS whose inferred `type` is `undefined`; widening it there would make
// the prop required for every one of its callers.
const SECRET_INPUT_PROPS = { type: 'password' };

// Shown under each secret only when the org already has one stored, which is the case where blank is a
// meaningful choice rather than an omission.
const KEEP_STORED_SECRET_HELPER_TEXT = 'Leave blank to keep the stored value.';

// The PUT body of the sysconfig config route, taken off the generated contract rather than restated,
// so a field renamed, added or removed server-side is a tsc error here instead of a claim in a
// comment. Every field is non-null on the way out: the route types the optional ones `T | None`
// because a credentials-only rotation may omit them, but this form always submits all of these.
//
// All but `webhook_signing_secret`, which this form no longer carries. Stripe mints that secret when
// the org's event destination is created, and the backend writes it there; a field for it could only
// ever overwrite a working secret with a typo. The route still accepts one, for an org whose
// destination was made by hand before this screen could make it.
export type StripeConfigurationFormValues = {
  [Field in keyof Required<Omit<StripeConfigurationArguments, 'webhook_signing_secret'>>]: NonNullable<
    StripeConfigurationArguments[Field]
  >;
};

interface StripeSetupDialogProps {
  onSubmit: (configuration: StripeConfigurationFormValues) => Promise<void> | void;
  onClose: () => void;
  configuration?: StripeConfigurationResponseStripeConfiguration | null;
}

const StripeSetupDialog: React.FC<StripeSetupDialogProps> = ({ onSubmit, onClose, configuration }) => {
  const { classes } = useStyles();
  // An org being configured for the first time must supply the secret key; one already configured has
  // it stored, and a blank field asks the write route to keep what it holds. Which is what lets
  // everything else on this form - the descriptor, the rails, and the Enabled switch above all - be
  // edited by someone who does not have the live API key to hand.
  const isFirstTimeSetup = !configuration;

  return (
    <Formik
      initialValues={{
        // The stored secret key is never returned by the API, so it always starts blank. Left blank it
        // is omitted from the save rather than cleared.
        secret_key: '',
        financial_account_external_id: configuration?.financial_account_external_id || '',
        // Starts empty on a first-time setup rather than pre-filled: which currency the treasury holds
        // is something only whoever is configuring the org knows, and a pre-selected code is one an
        // admin can leave in place without ever having read it. The column has no default behind it
        // either, so the write route rejects a create that names none.
        financial_account_currency: configuration?.financial_account_currency || '',
        statement_descriptor: configuration?.statement_descriptor || '',
        enabled_rails: configuration?.enabled_rails || DEFAULT_ENABLED_RAILS,
        // A configuration being set up for the first time is being turned on; an existing one keeps
        // whatever it is, so a credentials rotation cannot silently re-enable an org switched off.
        is_enabled: configuration ? configuration.is_enabled : true,
      }}
      validationSchema={Yup.object().shape({
        // Required only when there is nothing stored to keep - see `isFirstTimeSetup`.
        secret_key: isFirstTimeSetup ? Yup.string().required('Required') : Yup.string(),
        // Stripe Financial Account ids begin `fa_`; the write route rejects anything else, so catch it
        // here rather than after a round trip. It is a shape check, not proof the account exists.
        financial_account_external_id: Yup.string()
          .required('Required')
          .matches(/^fa_/, 'Should be a Stripe Financial Account id starting fa_'),
        // Which currency that account is funded in. It decides the `from.currency` on every payout,
        // which balance the pre-payout funds check reads, and whether a rail is a conversion - so an
        // org funded in one currency and configured as another has every payout refused by Stripe.
        // The dropdown is what constrains the *value* - it offers `SUPPORTED_CURRENCIES` and nothing
        // else - so this only has to say one must actually be picked. It starts blank, so this is what
        // stops the form being saved with no currency chosen.
        financial_account_currency: Yup.string().required('Required'),
        // Stripe's own bound on the descriptor it puts on the payee's bank statement, not the stored
        // column width: a longer value saves cleanly and then fails every payout for the org, at the
        // money call. Mirrors `STATEMENT_DESCRIPTOR_MIN_LENGTH` / `_MAX_LENGTH` on the write route,
        // which rejects the same values - the field is left optional here and there, so the minimum
        // is only applied to a value that was actually entered.
        statement_descriptor: Yup.string()
          .max(STATEMENT_DESCRIPTOR_MAX_LENGTH, `Up to ${STATEMENT_DESCRIPTOR_MAX_LENGTH} characters`)
          .test(
            'stripe-minimum-length',
            `At least ${STATEMENT_DESCRIPTOR_MIN_LENGTH} characters`,
            (value) => !value || value.length >= STATEMENT_DESCRIPTOR_MIN_LENGTH
          ),
        enabled_rails: Yup.array().of(Yup.string().oneOf(STRIPE_RAILS)).min(1, 'At least one rail is required'),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit, values, setFieldValue }) => (
        <CardDialog
          title="Stripe Integration Details"
          isDialog
          preventClose={isSubmitting}
          maxWidth="sm"
          fullWidth
          onClose={onClose}
        >
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} className="mb-20">
            {isFirstTimeSetup
              ? 'The secret key is stored write-only and is never read back, so keep a copy outside the product. The webhook signing secret is not entered here - registering the event destination below creates it.'
              : 'The secret key is stored write-only and is never read back. Leave it blank to keep the stored one; fill it in only to replace it.'}
          </Text>
          <div className="grid gap-20">
            <TextFieldFormik
              id="secret_key"
              label="Secret Key"
              helperText={isFirstTimeSetup ? undefined : KEEP_STORED_SECRET_HELPER_TEXT}
              InputProps={SECRET_INPUT_PROPS}
              className={classes.textField}
              fullWidth
            />
          </div>
          <div className="grid gap-20">
            <TextFieldFormik
              id="financial_account_external_id"
              label="Financial Account ID"
              className={classes.textField}
              fullWidth
            />
          </div>
          <div className="grid gap-20">
            {/* A select over the product's own generated currency list rather than a free-text code:
                the same `SUPPORTED_CURRENCIES` the payments configuration selects from, so a code this
                product does not deal in cannot be typed here at all.

                Not quite the write route's accepted set, and the gap is one entry: the route takes
                `SUPPORTED_CURRENCIES` intersected with the codes CLDR still knows, and `STP` (withdrawn
                for `STN` in 2018) is in this list and not in that one, so picking Sao Tome yields a 400
                rather than an inline error. Left as-is deliberately - CLDR is not available to the
                browser, and the nearest FE-side source (`Intl.supportedValuesOf('currency')`) drops
                `XAG`/`XAU`/`XPD`/`XPT` as well, which the route does accept, so deriving from it would
                hide four valid codes to spare one invalid one. Closing the gap properly means serving
                the accepted set from the backend. */}
            <TextFieldFormik
              id="financial_account_currency"
              label="Financial Account Currency"
              helperText="The currency the Financial Account is funded in - every payout is paid out of it."
              className={classes.textField}
              select
              fullWidth
            >
              {/* @ts-expect-error TextFieldFormik (JS) is not typed for select children */}
              {SUPPORTED_CURRENCIES.map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </TextFieldFormik>
          </div>
          <div className="grid gap-20">
            <TextFieldFormik
              id="statement_descriptor"
              label="Statement Descriptor"
              helperText={`What the payee sees on their bank statement. ${STATEMENT_DESCRIPTOR_MIN_LENGTH}-${STATEMENT_DESCRIPTOR_MAX_LENGTH} characters; leave blank to use the claim reference.`}
              className={classes.textField}
              fullWidth
            />
          </div>
          <div className="grid gap-20">
            <MultiSelectField<StripeRail>
              id={ENABLED_RAILS_FIELD_ID}
              label="Enabled Rails"
              value={values.enabled_rails}
              options={STRIPE_RAILS}
              onChange={(newRails) => setFieldValue(ENABLED_RAILS_FIELD_ID, newRails)}
              renderValue={(selected) => selected.map((rail) => STRIPE_RAIL_DISPLAY_NAMES[rail]).join(', ')}
              renderOption={(rail) => STRIPE_RAIL_DISPLAY_NAMES[rail]}
              className={classes.textField}
              fullWidth
            />
          </div>
          <div className="grid gap-20">
            {/* The off switch for the integration: cleared, the payout cron stops picking this org
                up while its credentials and rails stay as they are. */}
            <SwitchFormik id="is_enabled" label="Enabled" />
          </div>
          <div className="mt-px flex w-full justify-end">
            <CancelButton disabled={isSubmitting} onClick={onClose} />
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default StripeSetupDialog;
